import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export const useProductsOnTheWayList = (params: any = {}, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const { page, limit, sortBy, search, sales_staff, start_date, end_date, hide_arrived, has_remaining_stock, product_group } = params;

  const requestParams = {
    company_id: user?.company_id,
    page,
    limit,
    sortBy,
    search,
    sales_staff,
    start_date,
    end_date,
    hide_arrived,
    has_remaining_stock,
    product_group,
  };
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/list';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['products-on-the-way-list', params], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

export const usePotwProductGroups = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/product-groups';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: { company_id: user?.company_id } });
    return res?.data || [];
  };
  return useQuery(['potw-product-groups'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
    enabled: !!user?.company_id,
  });
};

export const useProductsOnTheWayCreate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/create';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useProductsOnTheWayUpdate = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/update';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useProductsOnTheWayDelete = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/delete';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useSalesStaffList = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { id: user?.company_id, limit: 1000 };
  const requestUrl = REACT_APP_API_URL + '/company/users/saleslist';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery(['sales-staff-list'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
    enabled: !!user?.company_id,
  });
};

export const useProductsOnTheWayMarkArrived = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/mark-arrived';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useProductsOnTheWayAddSale = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/add-sale';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useProductsOnTheWayUpdateSale = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/update-sale';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useProductsOnTheWayDeleteSale = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/delete-sale';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id });
    },
    { onError, onSuccess },
  );
};

export const useProductsOnTheWaySalesList = (onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/sales-list';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError },
  );
};

export const useProductsOnTheWayActivityLogs = (onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/activity-logs';
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, formPayload);
    },
    { onError },
  );
};

export const usePotwImportExcel = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/import-excel';
  return useMutation(
    (formData: FormData) => {
      return axios.post(`${requestUrl}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    { onError, onSuccess },
  );
};

export const usePotwPreviewExcel = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/products-on-the-way/preview-excel';
  return useMutation(
    (formData: FormData) => {
      return axios.post(`${requestUrl}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    { onError, onSuccess },
  );
};
